import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TuneIcon from "@mui/icons-material/Tune";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicIcon from "@mui/icons-material/Public";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KingBedOutlinedIcon from "@mui/icons-material/KingBedOutlined";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CheckIcon from "@mui/icons-material/Check";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import "./WLSearchbar.css";

const STATUS_COLORS = {
  for_sale: "#1e3a6e",
  under_bid: "#E27C00",
  under_option: "#E27C00",
  sold: "#9CA3AF",
  sold_under_reservation: "#9CA3AF",
};

const STATUS_LABELS = {
  for_sale: "For Sale",
  under_bid: "Under bid",
  under_option: "Under option",
  sold: "Sold",
  sold_under_reservation: "Sold (Reservation)",
};

const ENERGY_LABEL_KEYS = ["Minimaal Energie Label", "Minimaal Energielabel", "Minimum Energy Label"];
const BEDROOM_KEYS = ["Bedrooms", "Slaapkamers"];
const BATHROOM_KEYS = ["Bathrooms", "Badkamers"];
const BUILD_YEAR_KEYS = ["Bouwjaar", "Building Year"];

const formatArea = (value) => {
  if (value == null) return null;
  return String(value).replace(/m2\b/gi, "m²");
};

const findAttr = (attributes, keys) =>
  attributes.find((a) => keys.includes(a.key));

const QUICK_CITIES = [
  { name: "Amsterdam", lat: 52.37587792280813, lng: 4.90650338501307 },
  { name: "Rotterdam", lat: 51.90581441693896, lng: 4.466379056341172 },
  { name: "Utrecht",   lat: 52.09089006181871, lng: 5.122323994027422 },
];

const ENERGY_LABEL_VALUES = ["A+++", "A++", "A+", "A", "B", "C", "D", "E", "F", "G"];

const FILTER_SUGGESTIONS = [
  ...[1, 2, 3, 4, 5].map((n) => ({
    id: `bedrooms-${n}`,
    label: `${n}+ bedroom${n > 1 ? "s" : ""}`,
    category: "Bedrooms",
    Icon: KingBedOutlinedIcon,
    payload: { type: "bedrooms", value: n },
    keywords: [
      `${n} bedroom`, `${n} bedrooms`, `${n}+ bedroom`, `${n}+ bedrooms`,
      `${n} bed`, `${n} br`, `${n} bdr`, `${n}br`, `${n}bd`,
    ],
  })),
  ...[40, 60, 80, 100, 150].map((n) => ({
    id: `area-${n}`,
    label: `${n}+ m²`,
    category: "Minimum area",
    Icon: SquareFootIcon,
    payload: { type: "area", value: n },
    keywords: [
      `${n}m2`, `${n} m2`, `${n}m²`, `${n} m²`, `${n} sqm`, `${n}sqm`,
      `${n} square meter`, `${n} square meters`, `${n}+ m²`, `${n}+ m2`,
    ],
  })),
  { id: "type-house", label: "House", category: "Property type", Icon: HomeOutlinedIcon, payload: { type: "type", value: "House" }, keywords: ["house", "huis"] },
  { id: "type-apartment", label: "Apartment", category: "Property type", Icon: HomeOutlinedIcon, payload: { type: "type", value: "Apartment" }, keywords: ["apartment", "appartement", "flat"] },
  { id: "type-room", label: "Room", category: "Property type", Icon: HomeOutlinedIcon, payload: { type: "type", value: "Room" }, keywords: ["room", "kamer"] },
  { id: "type-garage", label: "Garage", category: "Property type", Icon: HomeOutlinedIcon, payload: { type: "type", value: "Garage" }, keywords: ["garage"] },
  { id: "status-available", label: "Available", category: "Listing status", Icon: LocalOfferOutlinedIcon, payload: { type: "status", value: "available" }, keywords: ["available", "for sale", "open", "te koop"] },
  { id: "status-sale_pending", label: "Under bid", category: "Listing status", Icon: LocalOfferOutlinedIcon, payload: { type: "status", value: "sale_pending" }, keywords: ["under bid", "bid", "pending", "onder bod"] },
  { id: "status-sold", label: "Sold", category: "Listing status", Icon: LocalOfferOutlinedIcon, payload: { type: "status", value: "sold" }, keywords: ["sold", "verkocht"] },
  ...ENERGY_LABEL_VALUES.map((label) => ({
    id: `energy-${label}`,
    label: `Label ${label}`,
    category: "Energy label",
    Icon: BoltIcon,
    payload: { type: "energyLabel", value: label },
    keywords: [
      `label ${label.toLowerCase()}`,
      `energy label ${label.toLowerCase()}`,
      `energy ${label.toLowerCase()}`,
      `energielabel ${label.toLowerCase()}`,
    ],
  })),
];

const MAX_FILTER_SUGGESTIONS = 5;

const matchFilterSuggestions = (rawQuery) => {
  const q = rawQuery.trim().toLowerCase();
  if (q.length < 2) return [];
  const out = [];
  for (const s of FILTER_SUGGESTIONS) {
    const hit = s.keywords.some((kw) => {
      const k = kw.toLowerCase();
      return k.includes(q) || q.includes(k);
    });
    if (hit) out.push(s);
    if (out.length >= MAX_FILTER_SUGGESTIONS) break;
  }
  return out;
};

const AI_SUGGESTED_PROMPTS = [
  "3 bedrooms, family-friendly, under €700k",
  "Quiet canal-side with energy label B+",
  "Walking distance to Vondelpark, no erfpacht",
  "Recent build with a balcony, ready to move in",
  "Monument property with character",
  "Lowest-risk option under €500k",
];

const AiLoadingView = ({ query, onClose }) => (
  <>
    <div className="wl-mapsettings-header">
      <div>
        <h2 className="wl-mapsettings-title wl-aisearch-title">
          <span className="wl-aisearch-title-icon">
            <AutoAwesomeIcon fontSize="small" />
          </span>
          AI picks for you
        </h2>
        <p className="wl-mapsettings-subtitle">
          {query ? (
            <>For: <strong>&ldquo;{query}&rdquo;</strong></>
          ) : (
            "Finding the best matches…"
          )}
        </p>
      </div>
      <button
        type="button"
        className="wl-mapsettings-close"
        aria-label="Close"
        onClick={onClose}
      >
        <CloseIcon fontSize="small" />
      </button>
    </div>

    <div className="wl-aipicks-loading-status" aria-live="polite">
      <AutoAwesomeIcon className="wl-aipicks-loading-icon" fontSize="small" />
      <span>Searching for matches…</span>
    </div>

    <div className="wl-aipicks-card">
      <div className="wl-aipicks-photo wl-aipicks-skeleton" />
      <div className="wl-aipicks-card-body">
        <div className="wl-aipicks-skeleton wl-aipicks-skeleton-line wl-aipicks-skeleton-line--price" />
        <div className="wl-aipicks-skeleton wl-aipicks-skeleton-line wl-aipicks-skeleton-line--lg" />
        <div className="wl-aipicks-skeleton wl-aipicks-skeleton-line wl-aipicks-skeleton-line--md" />
        <div className="wl-aipicks-skeleton-stats">
          <div className="wl-aipicks-skeleton wl-aipicks-skeleton-stat" />
          <div className="wl-aipicks-skeleton wl-aipicks-skeleton-stat" />
          <div className="wl-aipicks-skeleton wl-aipicks-skeleton-stat" />
          <div className="wl-aipicks-skeleton wl-aipicks-skeleton-stat" />
        </div>
      </div>
    </div>

    <div className="wl-aipicks-skeleton wl-aipicks-skeleton-reason" />
  </>
);

const AiResultsView = ({ query, listings, idx, onIdxChange, onClose, onNewSearch, onViewDetails }) => {
  const total = listings.length;

  if (total === 0) {
    return (
      <>
        <div className="wl-mapsettings-header">
          <div>
            <h2 className="wl-mapsettings-title wl-aisearch-title">
              <span className="wl-aisearch-title-icon">
                <AutoAwesomeIcon fontSize="small" />
              </span>
              AI picks for you
            </h2>
            <p className="wl-mapsettings-subtitle">
              For: <strong>&ldquo;{query}&rdquo;</strong>
            </p>
          </div>
          <button
            type="button"
            className="wl-mapsettings-close"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="wl-aipicks-empty">No listings matched your search.</div>
        <div className="wl-aipicks-footer">
          <button
            type="button"
            className="wl-aipicks-secondary"
            onClick={onNewSearch}
          >
            <RestartAltIcon fontSize="small" />
            <span>New search</span>
          </button>
        </div>
      </>
    );
  }

  const safeIdx = Math.min(idx, total - 1);
  const listing = listings[safeIdx];
  const goPrev = () => onIdxChange((safeIdx - 1 + total) % total);
  const goNext = () => onIdxChange((safeIdx + 1) % total);

  const image = (listing.files || [])
    .map((f) => f?.t_realmex_data_firehose_file_path)
    .find(Boolean);

  const attributes = listing.attributes || [];
  const energyAttr = findAttr(attributes, ENERGY_LABEL_KEYS);
  const bedroomsAttr = findAttr(attributes, BEDROOM_KEYS);
  const bathroomsAttr = findAttr(attributes, BATHROOM_KEYS);
  const buildYearAttr = findAttr(attributes, BUILD_YEAR_KEYS);
  const area = listing.area;
  const soldStatus = listing.sold_status;
  const statusColor = soldStatus ? STATUS_COLORS[soldStatus] : null;
  const statusLabel = soldStatus ? STATUS_LABELS[soldStatus] ?? soldStatus : null;

  return (
    <>
      <div className="wl-mapsettings-header">
        <div>
          <h2 className="wl-mapsettings-title wl-aisearch-title">
            <span className="wl-aisearch-title-icon">
              <AutoAwesomeIcon fontSize="small" />
            </span>
            AI picks for you
          </h2>
          <p className="wl-mapsettings-subtitle">
            For: <strong>&ldquo;{query}&rdquo;</strong>
          </p>
        </div>
        <button
          type="button"
          className="wl-mapsettings-close"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="wl-aipicks-pager">
        <button
          type="button"
          className="wl-aipicks-pager-btn"
          onClick={goPrev}
          aria-label="Previous match"
          disabled={total <= 1}
        >
          <ChevronLeftIcon fontSize="small" />
        </button>
        <div className="wl-aipicks-pager-text">
          <div className="wl-aipicks-pager-label">
            MATCH {safeIdx + 1} OF {total}
          </div>
          <div className="wl-aipicks-pager-dots">
            {listings.map((_, i) => (
              <span
                key={i}
                className={`wl-aipicks-pager-dot${i === safeIdx ? " is-active" : ""}`}
              />
            ))}
          </div>
        </div>
        <button
          type="button"
          className="wl-aipicks-pager-btn"
          onClick={goNext}
          aria-label="Next match"
          disabled={total <= 1}
        >
          <ChevronRightIcon fontSize="small" />
        </button>
      </div>

      <div className="wl-aipicks-card">
        <div className="wl-aipicks-photo">
          {image ? (
            <img src={image} alt={listing.title || "listing"} />
          ) : (
            <div className="wl-aipicks-photo-empty" />
          )}
          {energyAttr && (
            <span className="wl-aipicks-energy">{energyAttr.value}</span>
          )}
          {statusLabel && statusColor && (
            <span
              className="wl-aipicks-status"
              style={{ color: statusColor }}
            >
              <span
                className="wl-aipicks-status-dot"
                style={{ background: statusColor }}
              />
              {statusLabel}
            </span>
          )}
        </div>
        <div className="wl-aipicks-card-body">
          {listing.price_main && (
            <div className="wl-aipicks-price">{listing.price_main}</div>
          )}
          {listing.title && (
            <div className="wl-aipicks-address">{listing.title}</div>
          )}
          {(listing.postal || listing.city) && (
            <div className="wl-aipicks-location">
              {[listing.postal, listing.city].filter(Boolean).join(" · ")}
            </div>
          )}
          <div className="wl-aipicks-stats">
            {bedroomsAttr?.value != null && (
              <div className="wl-aipicks-stat">
                <strong>{bedroomsAttr.value}</strong>
                <span>bd</span>
              </div>
            )}
            {bathroomsAttr?.value != null && (
              <div className="wl-aipicks-stat">
                <strong>{bathroomsAttr.value}</strong>
                <span>ba</span>
              </div>
            )}
            {area != null && (
              <div className="wl-aipicks-stat">
                <strong>{formatArea(area)}</strong>
              </div>
            )}
            {buildYearAttr?.value != null && (
              <div className="wl-aipicks-stat">
                <strong>{buildYearAttr.value}</strong>
              </div>
            )}
          </div>
        </div>
      </div>

      {listing.reason && (
        <div className="wl-aipicks-reason">
          <div className="wl-aipicks-reason-label">
            <AutoAwesomeIcon className="wl-aipicks-reason-icon" fontSize="small" />
            <span>WHY THIS MATCHES</span>
          </div>
          <div className="wl-aipicks-reason-text">{listing.reason}</div>
        </div>
      )}

      <div className="wl-aipicks-footer">
        <button
          type="button"
          className="wl-aipicks-secondary"
          onClick={onNewSearch}
        >
          <RestartAltIcon fontSize="small" />
          <span>New search</span>
        </button>
        <button
          type="button"
          className="wl-aipicks-primary"
          onClick={() => onViewDetails?.(listing)}
        >
          <span>View full details</span>
          <ChevronRightIcon fontSize="small" />
        </button>
      </div>
    </>
  );
};

const EMPTY_FILTERS = { bedroom: null, area: null, types: [], statuses: [], energyLabels: [] };

const isFilterApplied = (payload, filters) => {
  switch (payload.type) {
    case "bedrooms":
      return filters.bedroom === payload.value;
    case "area":
      return filters.area === payload.value;
    case "type":
      return (filters.types || []).includes(payload.value);
    case "status":
      return (filters.statuses || []).includes(payload.value);
    case "energyLabel":
      return (filters.energyLabels || []).includes(payload.value);
    default:
      return false;
  }
};

const WLSearchbar = ({
  selectedCity,
  onCitySelect,
  listingMode = "all",
  onViewChange,
  onAdd,
  onSelectListing,
  filters = EMPTY_FILTERS,
  onBedroomApply,
  onAreaApply,
  onTypeApply,
  onStatusApply,
  onEnergyLabelApply,
  aiOpenRequest = 0,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftListingMode, setDraftListingMode] = useState(listingMode);
  const [draftCity, setDraftCity] = useState(selectedCity);
  const [quickCityCounts, setQuickCityCounts] = useState({});
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [aiResults, setAiResults] = useState(null);
  const [aiIdx, setAiIdx] = useState(0);
  const inputRef = useRef(null);

  const filterMatches = useMemo(
    () => (open && inputValue ? matchFilterSuggestions(inputValue) : []),
    [open, inputValue]
  );

  const toggleFilter = useCallback(
    (suggestion) => {
      const { type, value } = suggestion.payload;
      const applied = isFilterApplied(suggestion.payload, filters);
      switch (type) {
        case "bedrooms":
          onBedroomApply?.(applied ? null : value);
          break;
        case "area":
          onAreaApply?.(applied ? null : value);
          break;
        case "type": {
          const curr = filters.types || [];
          const next = applied ? curr.filter((v) => v !== value) : [...curr, value];
          onTypeApply?.(next);
          break;
        }
        case "status": {
          const curr = filters.statuses || [];
          const next = applied ? curr.filter((v) => v !== value) : [...curr, value];
          onStatusApply?.(next);
          break;
        }
        case "energyLabel": {
          const curr = filters.energyLabels || [];
          const next = applied ? curr.filter((v) => v !== value) : [...curr, value];
          onEnergyLabelApply?.(next);
          break;
        }
        default:
          break;
      }
    },
    [filters, onBedroomApply, onAreaApply, onTypeApply, onStatusApply, onEnergyLabelApply]
  );
  const { sendRequest } = useHttpNoAuth();
  const {
    sendRequest: sendAiSearch,
    isLoading: aiSearching,
    error: aiError,
  } = useHttpNoAuth();

  const runAiSearch = useCallback(() => {
    const trimmed = aiPrompt.trim();
    if (!trimmed) return;
    setAiQuery(trimmed);
    setAiResults(null);
    setAiIdx(0);
    sendAiSearch(
      {
        url: `${process.env.REACT_APP_BACKEND_URL}/realmex/ai-search?question=${encodeURIComponent(trimmed)}`,
      },
      (data) => {
        setAiResults(data?.listings || []);
      }
    );
  }, [aiPrompt, sendAiSearch]);

  const resetAiSearch = useCallback(() => {
    setAiResults(null);
    setAiQuery("");
    setAiIdx(0);
  }, []);

  const closeAiSheet = useCallback(() => {
    setAiOpen(false);
    setAiPrompt("");
    resetAiSearch();
  }, [resetAiSearch]);

  const closeAiSheetKeepingState = useCallback(() => {
    setAiOpen(false);
  }, []);

  useEffect(() => {
    if (aiOpenRequest > 0) setAiOpen(true);
  }, [aiOpenRequest]);

  useEffect(() => {
    if (!settingsOpen) return;
    setDraftListingMode(listingMode);
    setDraftCity(selectedCity || null);
  }, [settingsOpen, listingMode, selectedCity]);

  useEffect(() => {
    if (!settingsOpen) return;
    sendRequest(
      { url: `${process.env.REACT_APP_BACKEND_URL}/realmex/search_cities?q=` },
      (data) => {
        const counts = {};
        (data ?? []).forEach((c) => {
          counts[c.city] = c.listing_count;
        });
        setQuickCityCounts(counts);
      }
    );
  }, [settingsOpen, sendRequest]);

  useEffect(() => {
    if (!settingsOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setSettingsOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [settingsOpen]);

  useEffect(() => {
    if (!settingsOpen) return;
    document.body.classList.add("wl-mapsettings-open");
    return () => document.body.classList.remove("wl-mapsettings-open");
  }, [settingsOpen]);

  useEffect(() => {
    if (!aiOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeAiSheet();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [aiOpen, closeAiSheet]);

  useEffect(() => {
    if (!aiOpen) return;
    document.body.classList.add("wl-mapsettings-open");
    return () => document.body.classList.remove("wl-mapsettings-open");
  }, [aiOpen]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const timer = setTimeout(() => {
      sendRequest(
        { url: `${process.env.REACT_APP_BACKEND_URL}/realmex/search_cities?q=${encodeURIComponent(inputValue)}` },
        (data) => {
          setCities((data ?? []).map((c) => ({ name: c.city, province: c.province, listings: c.listing_count, lat: c.lat, lng: c.lng })));
          setLoading(false);
        }
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, open, sendRequest]);

  function handleSelect(city) {
    onCitySelect && onCitySelect({ name: city.name, lat: city.lat, lng: city.lng });
    setInputValue(city.name);
    setOpen(false);
  }

  function handleBlur() {
    setTimeout(() => setOpen(false), 150);
  }

  function highlight(text, query) {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="search-highlight">{text.slice(idx, idx + query.length)}</mark>
        {text.slice(idx + query.length)}
      </>
    );
  }

  return (
    <div className="search">
      <span className="search-icon">
        <SearchIcon fontSize="small" />
      </span>
      <input
        ref={inputRef}
        value={inputValue}
        placeholder="Search a city, or describe your ideal home..."
        onChange={(e) => {
          setInputValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
      />
      {open && inputValue && (
        <button
          className="search-clear"
          onMouseDown={(e) => {
            e.preventDefault();
            setInputValue("");
            setCities([]);
            setOpen(false);
            inputRef.current?.focus();
          }}
        >
          <CloseIcon fontSize="inherit" />
        </button>
      )}
      <div className={`search-pills${open ? " search-pills--hidden" : ""}`}>
        {QUICK_CITIES.map((city) => (
          <button
            key={city.name}
            type="button"
            className={`search-pill${selectedCity?.name === city.name ? " active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onCitySelect && onCitySelect(city);
              setOpen(false);
            }}
          >
            {city.name}
          </button>
        ))}
      </div>
      <div className="search-mobile-actions">
        <button
          type="button"
          className="search-icon-btn search-icon-btn--ai"
          aria-label="AI search"
          onClick={() => setAiOpen(true)}
        >
          <AutoAwesomeIcon fontSize="small" />
        </button>
        <button
          type="button"
          className="search-icon-btn"
          aria-label="Map settings"
          onClick={() => setSettingsOpen(true)}
        >
          <TuneIcon fontSize="small" />
        </button>
      </div>
      {open && inputValue && (
        <div className="search-dropdown-wrapper">
          <div className="search-dropdown">
            {filterMatches.length > 0 && (
              <>
                <div className="search-dropdown-section">Filters</div>
                {filterMatches.map((f) => {
                  const isApplied = isFilterApplied(f.payload, filters);
                  const Icon = f.Icon;
                  return (
                    <div
                      key={f.id}
                      className={`search-filter-item${isApplied ? " applied" : ""}`}
                      role="button"
                      tabIndex={0}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        toggleFilter(f);
                      }}
                    >
                      <span className="search-filter-icon">
                        <Icon fontSize="small" />
                      </span>
                      <div className="search-filter-info">
                        <span className="search-filter-label">{f.label}</span>
                        <span className="search-filter-category">{f.category}</span>
                      </div>
                      <span
                        className={`search-filter-action${isApplied ? " applied" : ""}`}
                        aria-hidden="true"
                      >
                        {isApplied ? (
                          <>
                            <CheckIcon fontSize="inherit" />
                            <span>Applied</span>
                          </>
                        ) : (
                          <span>APPLY</span>
                        )}
                      </span>
                    </div>
                  );
                })}
                <hr className="search-dropdown-divider" />
              </>
            )}
            <div className="search-dropdown-section">Cities</div>
            {!loading && cities.length === 0 && (
              <div className="search-no-results">No cities match "{inputValue}"</div>
            )}
            {cities.map((city) => (
              <div
                key={city.name}
                className={`search-city-item${selectedCity?.name === city.name ? " selected" : ""}${city.listings === 0 ? " disabled" : ""}`}
                onMouseDown={() => city.listings > 0 && handleSelect(city)}
              >
                <span className="search-city-icon">
                  <ApartmentIcon fontSize="small" />
                </span>
                <div className="search-city-info">
                  <span className="search-city-name">{highlight(city.name, inputValue)}</span>
                  <span className="search-city-province">{city.province}</span>
                </div>
                <span className="search-city-listings">
                  <strong>{city.listings}</strong> listings
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {settingsOpen && (
        <div
          className="wl-mapsettings-overlay"
          onClick={() => setSettingsOpen(false)}
        >
          <div
            className="wl-mapsettings-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Map settings"
          >
            <div className="wl-mapsettings-header">
              <div>
                <h2 className="wl-mapsettings-title">Map settings</h2>
                <p className="wl-mapsettings-subtitle">
                  Choose what shows on the map
                </p>
              </div>
              <button
                type="button"
                className="wl-mapsettings-close"
                aria-label="Close"
                onClick={() => setSettingsOpen(false)}
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>

            <div className="wl-mapsettings-section-label">Show me</div>
            <div className="wl-mapsettings-show-grid">
              <button
                type="button"
                className={`wl-mapsettings-show-card${
                  draftListingMode === "my" ? " is-active" : ""
                }`}
                onClick={() => setDraftListingMode("my")}
              >
                <span className="wl-mapsettings-show-icon">
                  <PersonOutlineIcon fontSize="small" />
                </span>
                <span className="wl-mapsettings-show-text">
                  <span className="wl-mapsettings-show-title">My listings</span>
                  <span className="wl-mapsettings-show-desc">
                    Properties assigned to me
                  </span>
                </span>
              </button>
              <button
                type="button"
                className={`wl-mapsettings-show-card${
                  draftListingMode === "all" ? " is-active" : ""
                }`}
                onClick={() => setDraftListingMode("all")}
              >
                <span className="wl-mapsettings-show-icon">
                  <PublicIcon fontSize="small" />
                </span>
                <span className="wl-mapsettings-show-text">
                  <span className="wl-mapsettings-show-title">All listings</span>
                  <span className="wl-mapsettings-show-desc">
                    Everything on the market
                  </span>
                </span>
              </button>
            </div>

            <div className="wl-mapsettings-section-label">Zoom to city</div>
            <div className="wl-mapsettings-city-list">
              {QUICK_CITIES.map((city) => {
                const isSelected = draftCity?.name === city.name;
                const count = quickCityCounts[city.name];
                return (
                  <button
                    key={city.name}
                    type="button"
                    className={`wl-mapsettings-city-row${
                      isSelected ? " is-active" : ""
                    }`}
                    onClick={() => setDraftCity(city)}
                  >
                    <span className="wl-mapsettings-city-icon">
                      <LocationOnIcon fontSize="small" />
                    </span>
                    <span className="wl-mapsettings-city-text">
                      <span className="wl-mapsettings-city-name">
                        {city.name}
                      </span>
                      {count !== undefined && (
                        <span className="wl-mapsettings-city-meta">
                          {count} {count === 1 ? "listing" : "listings"}
                        </span>
                      )}
                    </span>
                    <span className="wl-mapsettings-city-check">
                      {isSelected ? (
                        <CheckCircleIcon fontSize="small" />
                      ) : (
                        <RadioButtonUncheckedIcon fontSize="small" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="wl-mapsettings-footer">
              <button
                type="button"
                className="wl-mapsettings-btn wl-mapsettings-btn--ghost"
                onClick={() => {
                  setDraftListingMode("all");
                  setDraftCity(null);
                }}
              >
                Reset
              </button>
              <button
                type="button"
                className="wl-mapsettings-btn wl-mapsettings-btn--primary"
                onClick={() => {
                  if (draftListingMode !== listingMode) {
                    onViewChange?.(draftListingMode);
                  }
                  if ((draftCity?.name ?? null) !== (selectedCity?.name ?? null)) {
                    onCitySelect?.(draftCity);
                  }
                  setSettingsOpen(false);
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {aiOpen && (
        <div
          className="wl-mapsettings-overlay"
          onClick={closeAiSheet}
        >
          <div
            className="wl-mapsettings-sheet wl-aisearch-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Ask AI to find a listing"
          >
            {aiSearching ? (
              <AiLoadingView query={aiQuery} onClose={closeAiSheet} />
            ) : aiResults && !aiError ? (
              <AiResultsView
                query={aiQuery}
                listings={aiResults}
                idx={aiIdx}
                onIdxChange={setAiIdx}
                onClose={closeAiSheet}
                onNewSearch={resetAiSearch}
                onViewDetails={(listing) => {
                  onSelectListing?.(listing);
                  closeAiSheetKeepingState();
                }}
              />
            ) : (
              <>
                <div className="wl-mapsettings-header">
                  <div>
                    <h2 className="wl-mapsettings-title wl-aisearch-title">
                      <span className="wl-aisearch-title-icon">
                        <AutoAwesomeIcon fontSize="small" />
                      </span>
                      Ask AI to find a listing
                    </h2>
                    <p className="wl-mapsettings-subtitle">
                      Describe what you're looking for in plain language
                    </p>
                  </div>
                  <button
                    type="button"
                    className="wl-mapsettings-close"
                    aria-label="Close"
                    onClick={closeAiSheet}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>

                <textarea
                  className="wl-aisearch-textarea"
                  placeholder="e.g. 2-bedroom near a park, under €600k, ready to move in..."
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={3}
                  disabled={aiSearching}
                />

                <div className="wl-mapsettings-section-label">Suggested prompts</div>
                <div className="wl-aisearch-prompts">
                  {AI_SUGGESTED_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      className="wl-aisearch-prompt"
                      onClick={() => setAiPrompt(prompt)}
                      disabled={aiSearching}
                    >
                      <span className="wl-aisearch-prompt-icon">
                        <AutoAwesomeIcon fontSize="inherit" />
                      </span>
                      <span className="wl-aisearch-prompt-text">{prompt}</span>
                    </button>
                  ))}
                </div>

                {aiError && (
                  <div className="wl-aisearch-error">
                    Something went wrong while fetching results. Please try again.
                  </div>
                )}

                <button
                  type="button"
                  className="wl-aisearch-submit"
                  disabled={!aiPrompt.trim() || aiSearching}
                  onClick={runAiSearch}
                >
                  <AutoAwesomeIcon fontSize="small" />
                  <span>{aiSearching ? "Searching…" : "Search with AI"}</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WLSearchbar;
